import React, { forwardRef, useImperativeHandle } from 'react';
import { SearchState } from '@customTypes/search';
import { CheckboxCustomV2 as CheckboxCustom } from '@components/common/CheckboxCustom/CheckboxCustom';
import SRPFilters from '@components/SearchExperiment/SRPFilters/SRPFilters';
import {
  useSearchContext,
  PoolFeatures,
  PoolProductTypes,
  SpaProductTypes,
  SpaFeatures,
  ProductCategorySpecificFilters,
  AmenitiesFriendlyNames,
  HotelStarsFriendlyNames,
  VibesFriendlyNames,
  GuestRatingFriendlyNames,
} from '@context/SearchContext2025';

import {
  useAmplitudeApplyFilterTracker,
  useAmplitudeFilterCancelTracker,
} from '@hooks/useAmplitudeTrackers';

import { MobileSRPDrawerInterface } from './MobileSRPDrawer';

/**
 * MobileAllFilters component is a wrapper for the SRPFilters component.
 * This component conforms the MobileSRPDrawerInterface that is the unified way to
 * tap into the clear and apply button filter buttons.
 */

export const AllFiltersDrawer = forwardRef<MobileSRPDrawerInterface>((_, ref) => {
  const searchContext = useSearchContext();
  const { searchParams: searchState } = searchContext;
  const amplitudeSidebarApplyTracker = useAmplitudeApplyFilterTracker('srp_sidebar');
  const amplitudeSidebarCancelTracker = useAmplitudeFilterCancelTracker('srp_sidebar');

  useImperativeHandle(ref, () => ({
    onApply: (s: SearchState) => {
      amplitudeSidebarApplyTracker(s);
    },
    onClose: () => {
      amplitudeSidebarCancelTracker(searchState);
    },
  }));

  return <SRPFilters />;
});

/**
 * PoolFeaturesDrawer component is shown in the MobileSRPDrawer when the user
 * clicks on the Pool Features chip.
 */
export const PoolFeaturesDrawer = forwardRef<MobileSRPDrawerInterface>((_, ref) => {
  const searchContext = useSearchContext();
  const { searchParams } = searchContext;
  const { filters } = searchParams;
  const amplitudeApplyFilterTracker = useAmplitudeApplyFilterTracker('srp_quick_filters');
  const amplitudeCancelFilterTracker = useAmplitudeFilterCancelTracker('srp_quick_filters');

  // Product Category Specific Filters PRD:
  // https://docs.google.com/document/d/1W4gPkLtkh2yWSTrMQiKraXJQpwaXaPFV2l4Tii8Cc6g/edit?tab=t.0#heading=h.lj31hk6jbexf
  //
  // Per requirements here, we should not show the daybed or cabana
  // product type filters if we are searching for day rooms
  const shouldShowProductTypes = searchParams.product_category !== 'day room';

  // Get the setter function for each pool feature
  const getSetter = (key: string) => () => {
    searchContext.setAmenities({
      [key]: !filters.amenities[key as keyof typeof filters.amenities],
    });
  };

  const getProductTypeSetter = (productTypeId: number) => () => {
    if (filters.product_types.includes(productTypeId)) {
      searchContext.removeProductType(productTypeId);
    } else {
      searchContext.addProductType(productTypeId);
    }
  };

  // Clear the pool feature and product types
  const onClear = () => {
    searchContext.setAmenities({
      ...PoolFeatures.reduce((acc, feature) => {
        acc[feature] = false;
        return acc;
      }, {} as typeof filters.amenities),
    });
    Object.values(PoolProductTypes).forEach((productTypeId) =>
      searchContext.removeProductType(productTypeId));
  };

  useImperativeHandle(ref, () => ({
    onClear,
    onApply: (s: SearchState) => {
      amplitudeApplyFilterTracker(s);
    },
    onClose: () => {
      amplitudeCancelFilterTracker(searchParams);
    },
  }));

  return (
    <div className="flex flex-col gap-3 p-spacing-md">
      {PoolFeatures.slice(0, PoolFeatures.length - 3).map((feature) => (
        <div className="flex items-center font-rp-basetica" key={`pool-feature-${feature}`}>
          <CheckboxCustom
            label={AmenitiesFriendlyNames[feature as keyof typeof AmenitiesFriendlyNames]}
            isChecked={filters.amenities[feature as keyof typeof filters.amenities]}
            setIsChecked={getSetter(feature)}
            id={`pool-feature-${feature}`}
            isDarkBorder
          />
        </div>
      ))}

      {shouldShowProductTypes &&
        Object.entries(PoolProductTypes).map(([name, productTypeId]) => (
          <div className="flex items-center font-rp-basetica" key={`pool-product-type-${name}`}>
            <CheckboxCustom
              label={name}
              isChecked={filters.product_types.includes(productTypeId)}
              setIsChecked={getProductTypeSetter(productTypeId)}
              id={`pool-product-type-${name}`}
              isDarkBorder
            />
          </div>
        ))}

      {PoolFeatures.slice(PoolFeatures.length - 3).map((feature) => (
        <div className="flex items-center font-rp-basetica" key={`pool-feature-${feature}`}>
          <CheckboxCustom
            label={AmenitiesFriendlyNames[feature as keyof typeof AmenitiesFriendlyNames]}
            isChecked={filters.amenities[feature as keyof typeof filters.amenities]}
            setIsChecked={getSetter(feature)}
            id={`pool-feature-${feature}`}
            isDarkBorder
          />
        </div>
      ))}
    </div>
  );
});

/**
 * SpaServicesDrawer component is the drawer shown when the Spa Services chip is clicked.
 */
export const SpaServicesDrawer = forwardRef<MobileSRPDrawerInterface>((_, ref) => {
  const searchContext = useSearchContext();
  const amplitudeApplyFilterTracker = useAmplitudeApplyFilterTracker('srp_quick_filters');
  const amplitudeCancelFilterTracker = useAmplitudeFilterCancelTracker('srp_quick_filters');
  const {
    searchParams: { filters },
  } = searchContext;
  const getSetter = (key: number) => () => {
    const containsProductType = filters.product_types.includes(key);
    if (containsProductType) {
      searchContext.removeProductType(key);
    } else {
      searchContext.addProductType(key);
    }
  };

  const onClear = () => {
    // Clear all Spa Product Types
    Object.values(SpaProductTypes).forEach((productTypeId) =>
      searchContext.removeProductType(productTypeId));
  };

  useImperativeHandle(ref, () => ({
    onClear,
    onApply: (s: SearchState) => {
      amplitudeApplyFilterTracker(s);
    },
    onClose: () => {
      amplitudeCancelFilterTracker(searchContext.searchParams);
    },
  }));

  return (
    <div className="flex flex-col gap-3 p-spacing-md">
      {Object.entries(SpaProductTypes).map(([name, productTypeId]) => (
        <div className="flex items-center font-rp-basetica" key={`spa-service-${name}`}>
          <CheckboxCustom
            label={name}
            isChecked={filters.product_types.includes(productTypeId)}
            setIsChecked={getSetter(productTypeId)}
            id={`spa-service-${name}`}
            isDarkBorder
          />
        </div>
      ))}
    </div>
  );
});

/**
 * SpaFeaturesDrawer component is the drawer shown when the Spa Services chip is clicked.
 */
export const SpaFeaturesDrawer = forwardRef<MobileSRPDrawerInterface>((_, ref) => {
  const searchContext = useSearchContext();
  const amplitudeApplyFilterTracker = useAmplitudeApplyFilterTracker('srp_quick_filters');
  const amplitudeCancelFilterTracker = useAmplitudeFilterCancelTracker('srp_quick_filters');
  const {
    searchParams: { filters },
  } = searchContext;
  const getSetter = (key: string) => () => {
    searchContext.setAmenities({
      [key]: !filters.amenities[key as keyof typeof filters.amenities],
    });
  };

  const onClear = () => {
    // Clear all Spa Features
    searchContext.setAmenities({
      ...SpaFeatures.reduce((acc, feature) => {
        acc[feature] = false;
        return acc;
      }, {} as typeof filters.amenities),
    });
  };

  useImperativeHandle(ref, () => ({
    onClear,
    onApply: (s: SearchState) => {
      amplitudeApplyFilterTracker(s);
    },
    onClose: () => {
      amplitudeCancelFilterTracker(searchContext.searchParams);
    },
  }));

  return (
    <div className="flex flex-col gap-3 p-spacing-md">
      {SpaFeatures.map((feature) => (
        <div className="flex items-center font-rp-basetica" key={`spa-feature-${feature}`}>
          <CheckboxCustom
            label={AmenitiesFriendlyNames[feature]}
            isChecked={filters.amenities[feature]}
            setIsChecked={getSetter(feature)}
            id={`spa-feature-${feature}`}
            isDarkBorder
          />
        </div>
      ))}
    </div>
  );
});

/**
 * AmenitiesDrawer component is the content shown when the Amenities chip is clicked.
 */
export const AmenitiesDrawer = forwardRef<MobileSRPDrawerInterface>((_, ref) => {
  const searchContext = useSearchContext();
  const amplitudeApplyFilterTracker = useAmplitudeApplyFilterTracker('srp_quick_filters');
  const amplitudeCancelFilterTracker = useAmplitudeFilterCancelTracker('srp_quick_filters');
  const {
    searchParams: { filters, product_category: productCategory },
  } = searchContext;
  const getSetter = (key: string) => () => {
    searchContext.setAmenities({
      [key]: !filters.amenities[key as keyof typeof filters.amenities],
    });
  };

  // https://docs.google.com/document/d/1W4gPkLtkh2yWSTrMQiKraXJQpwaXaPFV2l4Tii8Cc6g/edit?tab=t.0#heading=h.lj31hk6jbexf
  //
  // Per the PRD here, Spa and Day Room categories should hide certain amenities.
  const excludeAmenities: Array<keyof typeof filters.amenities> = (() => {
    if (productCategory === 'spa') {
      return ['all-inclusive', 'beach', 'wheelchair', 'showers', 'lockers'];
    }

    if (productCategory === 'day room') {
      return ['showers', 'lockers'];
    }
    return [];
  })();

  const amenitiesToShow = (
    Object.keys(filters.amenities) as Array<keyof typeof filters.amenities>
  ).filter(
    (key) =>
      !excludeAmenities.includes(key as keyof typeof filters.amenities) &&
      !ProductCategorySpecificFilters.has(key as keyof typeof filters.amenities),
  );

  const onClear = () => {
    searchContext.setAmenities({
      ...amenitiesToShow.reduce((acc, key) => {
        acc[key as keyof typeof filters.amenities] = false;
        return acc;
      }, {} as typeof filters.amenities),
    });
  };

  useImperativeHandle(ref, () => ({
    onClear,
    onApply: (s: SearchState) => {
      amplitudeApplyFilterTracker(s);
    },
    onClose: () => {
      amplitudeCancelFilterTracker(searchContext.searchParams);
    },
  }));

  return (
    <div className="flex flex-col gap-3 p-spacing-md">
      {amenitiesToShow.map((id) => (
        <div className="flex items-center font-rp-basetica" key={`amenity-${id}`}>
          <CheckboxCustom
            label={AmenitiesFriendlyNames[id]}
            isChecked={filters.amenities[id]}
            setIsChecked={getSetter(id)}
            id={`amenity-${id}`}
            isDarkBorder
          />
        </div>
      ))}
    </div>
  );
});

/**
 * HotelStarsDrawer component is the drawer shown when the Hotel Stars chip is clicked.
 */
export const HotelStarsDrawer = forwardRef<MobileSRPDrawerInterface>((_, ref) => {
  const searchContext = useSearchContext();
  const amplitudeApplyFilterTracker = useAmplitudeApplyFilterTracker('srp_quick_filters');
  const amplitudeCancelFilterTracker = useAmplitudeFilterCancelTracker('srp_quick_filters');
  const {
    searchParams: { filters },
  } = searchContext;
  const getSetter = (key: keyof typeof filters.hotel_stars) => () => {
    searchContext.setHotelStars({
      [key]: !filters.hotel_stars[key],
    });
  };

  const onClear = () => {
    searchContext.setHotelStars({
      ...Object.keys(filters.hotel_stars).reduce((acc, key) => {
        acc[key as keyof typeof filters.hotel_stars] = false;
        return acc;
      }, {} as typeof filters.hotel_stars),
    });
  };

  useImperativeHandle(ref, () => ({
    onClear,
    onApply: (s: SearchState) => {
      amplitudeApplyFilterTracker(s);
    },
    onClose: () => {
      amplitudeCancelFilterTracker(searchContext.searchParams);
    },
  }));

  return (
    <div className="flex flex-col gap-3 p-spacing-md">
      {(Object.keys(filters.hotel_stars) as Array<keyof typeof filters.hotel_stars>).map(
        (feature) => (
          <div className="flex items-center font-rp-basetica" key={`hotel-stars-${feature}`}>
            <CheckboxCustom
              label={HotelStarsFriendlyNames[feature]}
              isChecked={filters.hotel_stars[feature]}
              setIsChecked={getSetter(feature)}
              id={`hotel-stars-${feature}`}
              isDarkBorder
            />
          </div>
        ),
      )}
    </div>
  );
});

/**
 * VibesDrawer component is the drawer shown when the Vibes chip is clicked.
 */
export const VibesDrawer = forwardRef<MobileSRPDrawerInterface>((_, ref) => {
  const searchContext = useSearchContext();
  const amplitudeApplyFilterTracker = useAmplitudeApplyFilterTracker('srp_quick_filters');
  const amplitudeCancelFilterTracker = useAmplitudeFilterCancelTracker('srp_quick_filters');
  const {
    searchParams: { filters },
  } = searchContext;
  const getSetter = (key: keyof typeof filters.vibes) => () => {
    searchContext.setVibes({
      [key]: !filters.vibes[key],
    });
  };

  const onClear = () => {
    searchContext.setVibes({
      ...Object.keys(filters.vibes).reduce((acc, key) => {
        acc[key as keyof typeof filters.vibes] = false;
        return acc;
      }, {} as typeof filters.vibes),
    });
  };

  useImperativeHandle(ref, () => ({
    onClear,
    onApply: (s: SearchState) => {
      amplitudeApplyFilterTracker(s);
    },
    onClose: () => {
      amplitudeCancelFilterTracker(searchContext.searchParams);
    },
  }));

  return (
    <div className="flex flex-col gap-3 p-spacing-md">
      {(Object.keys(filters.vibes) as Array<keyof typeof filters.vibes>).map((feature) => (
        <div className="flex items-center font-rp-basetica" key={`vibes-${feature}`}>
          <CheckboxCustom
            label={VibesFriendlyNames[feature]}
            isChecked={filters.vibes[feature]}
            setIsChecked={getSetter(feature)}
            id={`vibes-${feature}`}
            isDarkBorder
          />
        </div>
      ))}
    </div>
  );
});

/**
 * GuestRatingDrawer component is the drawer shown when the guest rating chip is clicked.
 */
export const GuestRatingDrawer = forwardRef<MobileSRPDrawerInterface>((_, ref) => {
  const searchContext = useSearchContext();
  const amplitudeApplyFilterTracker = useAmplitudeApplyFilterTracker('srp_quick_filters');
  const amplitudeCancelFilterTracker = useAmplitudeFilterCancelTracker('srp_quick_filters');
  const {
    searchParams: { filters },
  } = searchContext;
  const getSetter = (key: keyof typeof filters.guest_ratings) => () => {
    searchContext.setGuestRatings({
      [key]: !filters.guest_ratings[key],
    });
  };

  const onClear = () => {
    searchContext.setGuestRatings({
      ...Object.keys(filters.guest_ratings).reduce((acc, key) => {
        acc[key as keyof typeof filters.guest_ratings] = false;
        return acc;
      }, {} as typeof filters.guest_ratings),
    });
  };

  useImperativeHandle(ref, () => ({
    onClear,
    onApply: (s: SearchState) => {
      amplitudeApplyFilterTracker(s);
    },
    onClose: () => {
      amplitudeCancelFilterTracker(searchContext.searchParams);
    },
  }));

  return (
    <div className="flex flex-col gap-3 p-spacing-md">
      {(Object.keys(filters.guest_ratings) as Array<keyof typeof filters.guest_ratings>).map(
        (feature) => (
          <div className="flex items-center font-rp-basetica" key={`guestrating-${feature}`}>
            <CheckboxCustom
              label={GuestRatingFriendlyNames[feature]}
              isChecked={filters.guest_ratings[feature]}
              setIsChecked={getSetter(feature)}
              id={`guestrating-${feature}`}
              isDarkBorder
            />
          </div>
        ),
      )}
    </div>
  );
});
